import React from 'react';
import { TopPromptDto } from '../../services/api';
import { Badge } from '../ui/badge';
import { TrendingUp, TrendingDown, Heart, Star, BarChart3 } from 'lucide-react';

interface TopPromptsTableProps {
  data: TopPromptDto[];
  onPromptClick?: (id: number) => void;
}

function TrendIndicator({ change }: { change: string }) {
  const isPositive = change.startsWith('+');
  const isNegative = change.startsWith('-');
  
  if (isPositive) {
    return (
      <div className="flex items-center text-green-600 text-xs">
        <TrendingUp className="w-3 h-3 mr-1" />
        {change}
      </div>
    );
  }
  
  if (isNegative) {
    return (
      <div className="flex items-center text-red-600 text-xs">
        <TrendingDown className="w-3 h-3 mr-1" />
        {change}
      </div>
    );
  }
  
  return <span className="text-xs text-muted-foreground">{change}</span>;
}

export function TopPromptsTable({ data, onPromptClick }: TopPromptsTableProps) {
  return (
    <div className="space-y-4">
      {data.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No prompts data available
        </div>
      ) : (
        <div className="space-y-3">
          {data.map((prompt, index) => (
            <div
              key={prompt.id}
              className={`flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors${onPromptClick ? ' cursor-pointer' : ''}`}
              onClick={() => onPromptClick?.(prompt.id)}
            >
              <div className="flex items-center space-x-3 flex-1 min-w-0">
                <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                  <span className="text-sm font-semibold text-primary">
                    #{index + 1}
                  </span>
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <h4 className="font-medium text-sm truncate">
                      {prompt.title}
                    </h4>
                    <Badge variant="secondary" className="text-xs">
                      {prompt.category}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center space-x-4 mt-1">
                    <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                      <BarChart3 className="w-3 h-3" />
                      <span>{prompt.uses.toLocaleString()} uses</span>
                      <TrendIndicator change={prompt.usesChange} />
                    </div>
                    
                    <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                      <Heart className="w-3 h-3" />
                      <span>{prompt.likes.toLocaleString()} likes</span>
                      <TrendIndicator change={prompt.likesChange} />
                    </div>
                    
                    {prompt.ratingCount > 0 && (
                      <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                        <Star className="w-3 h-3" />
                        <span>{prompt.rating.toFixed(1)}</span>
                        <span>({prompt.ratingCount})</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex-shrink-0 text-right">
                <div className="text-xs text-muted-foreground">
                  by {prompt.submittedBy}
                </div>
                <div className="text-xs text-muted-foreground">
                  {new Date(prompt.createdAt).toLocaleDateString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}